import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Plus,
  Calendar,
  Eye,
  Search,
  DollarSign,
  User,
  ArrowLeft,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  X,
  RefreshCw,
  Building2,
  Users
} from 'lucide-react';
import { bondsAPI, custodianAPI, usersAPI, secondaryTransfersAPI, subscriptionAPI, walletAPI, investorAPI, formatCurrency, formatDate } from '../../services/api';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ClientTransfers Component
 * 
 * This component displays and manages secondary market transfers where the custodian's 
 * sub-investors (clients) are involved. Counterparties can be direct investors or other 
 * custodians. The backend automatically filters transfers based on the custodian's role, 
 * showing only:
 * 
 * 1. Transfers initiated by this custodian
 * 2. Transfers involving clients (sub-investors) managed by this custodian
 * 3. Transfers where this custodian is the initiator with a clientId set
 * 
 * For Buy transfers, the counterparty dropdown shows only investors who have allocations
 * for the selected bond (fetched from the allocations API).
 * 
 * This ensures custodians only see and can process transfers relevant to their operations.
 */
const ClientTransfers = () => {
  const navigate = useNavigate();
  const { user, getDisplayName } = useAuth();
  const queryClient = useQueryClient();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  // Success/Error modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // LEI Search states
  const [counterpartySearchTerm, setCounterpartySearchTerm] = useState('');
  const [showCounterpartySuggestions, setShowCounterpartySuggestions] = useState(false);
  const [recentlyUsedCounterparties, setRecentlyUsedCounterparties] = useState(() => {
    try {
      const stored = localStorage.getItem('recentCounterparties');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Simple MongoDB ObjectId validator (24 hex chars)
  const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  // Ethereum-style hex address checker
  const isHexAddress = (addr) => {
    return typeof addr === 'string' && /^0x[0-9a-fA-F]{40}$/.test(addr);
  };

  // Safely derive a human-readable display name from varied user object shapes
  const deriveDisplayName = (entity) => {
    if (!entity) return 'Unknown';
    if (typeof entity === 'string') return entity; // already an id or name
    // If there's an explicit string name
    if (typeof entity.name === 'string' && entity.name.trim()) return entity.name;
    if (typeof entity.displayName === 'string' && entity.displayName.trim()) return entity.displayName;
    if (typeof entity.fullName === 'string' && entity.fullName.trim()) return entity.fullName;
    // Corporate info
    if (entity.corporateInfo) {
      const c = entity.corporateInfo;
      if (c.companyName) return c.companyName;
      if (c.legalEntityName) return c.legalEntityName;
    }
    // Personal info
    if (entity.personalInfo) {
      const p = entity.personalInfo;
      const parts = [p.firstName, p.lastName].filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    if (entity.email) return entity.email;
    return entity._id || entity.id || 'Unknown';
  };

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    clientId: '',
    clientWalletId: '',
    counterpartyWalletId: '',
    bondId: '',
    transferType: 'Secondary', // Token transfers use "Secondary" instead of "Sell"/"Buy"
    counterpartyId: '',
    nominalValue: '',
    tokenAmount: '',
    price: '',
    tradeDate: new Date().toISOString().split('T')[0],
    valueDate: '',
    description: '',
    paymentReference: '',
    transferReason: '',
    transferDirection: 'Sell' // Track sell/buy direction separately for UI logic
  });

  // page-level logout removed

  // Fetch sub-investors (clients) managed by this custodian
  const { data: clientsData, isLoading: clientsLoading, error: clientsError } = useQuery({
    queryKey: ['custodian-clients'],
    queryFn: () => custodianAPI.getSubInvestors({ limit: 100 }),
    select: (data) => {
      // Handle the actual API response structure: { subInvestors: [...], pagination: {...} }
      let clients = [];
      if (data?.data?.subInvestors) {
        clients = data.data.subInvestors;
      } else if (data?.subInvestors) {
        clients = data.subInvestors;
      } else if (Array.isArray(data?.data)) {
        clients = data.data;
      } else if (Array.isArray(data)) {
        clients = data;
      }

      const processedClients = clients.map(client => ({
        id: client._id,
        name: deriveDisplayName(client),
        type: client.accountType === 'corporate' ? 'Corporate' : 'Individual',
        status: client.isActive ? 'Active' : 'Inactive',
        isActive: !!client.isActive,
        email: client.email
      })) || [];

      return processedClients;
    }
  });

  // NOTE: Removed custodian allocations API call as requested
  // This data is no longer fetched from /api/custodian/allocations
  const custodianBondsData = [];
  const custodianBondsLoading = false;

  // Fetch ALL finalized bonds (for Buy transfers)
  const { data: allFinalizedBonds, isLoading: finalizedBondsLoading, error: finalizedBondsError } = useQuery({
    queryKey: ['all-finalized-bonds'],
    queryFn: async () => {
      try {
        const response = await bondsAPI.getBonds({ status: 'Allocation Finalized', limit: 100 });
        return response;
      } catch (error) {
        throw error;
      }
    },
    select: (data) => {
      // Handle the bonds API response - extract from data.data.data.bonds
      let bonds = [];

      if (data?.data?.data?.bonds && Array.isArray(data.data.data.bonds)) {
        bonds = data.data.data.bonds;
      } else if (data?.data?.bonds && Array.isArray(data.data.bonds)) {
        bonds = data.data.bonds;
      } else if (data?.data?.data && Array.isArray(data.data.data)) {
        bonds = data.data.data;
      } else if (data?.data && Array.isArray(data.data)) {
        bonds = data.data;
      } else if (Array.isArray(data)) {
        bonds = data;
      } else {
        return [];
      }

      if (bonds.length === 0) {
        return [];
      }

      const processedBonds = bonds.map((bond, index) => {
        const processed = {
          id: bond._id, // Use _id as the main ID
          bondName: bond.bondName, // bondName field exists
          isin: bond.isin, // isin field exists
          issuer: bond.issuer?.email || 'Unknown Issuer', // issuer is an object with email
          totalNominalValue: bond.issueAmount || 0, // issueAmount is the total value
          profitRate: bond.couponRate || 0, // couponRate is the profit rate
          maturityDate: bond.maturityDate,
          productType: bond.productType, // productType field exists
          status: bond.status, // status field exists
          currency: bond.currency, // currency field exists
          issuePrice: bond.issuePrice, // issuePrice field exists
          bondNumber: bond.bondNumber // bondNumber field exists
        };

        return processed;
      });

      return processedBonds;
    }
  });

  // Fetch verified users who can be counterparties (direct investors and other custodians)
  const { data: counterpartiesData, isLoading: counterpartiesLoading, error: counterpartiesError } = useQuery({
    queryKey: ['custodian-counterparties'],
    queryFn: async () => {
      try {
        const response = await custodianAPI.getCounterparties();
        return response;
      } catch (error) {
        throw error;
      }
    },
    select: (data) => {
      if (!data) {
        return [];
      }

      // Handle the custodian API response structure
      // The API returns: { data: { success: true, data: [...], total: 14 } }
      let users = [];
      if (data?.data?.data && Array.isArray(data.data.data)) {
        users = data.data.data;
      } else if (data?.data && Array.isArray(data.data)) {
        users = data.data;
      } else if (data?.counterparties && Array.isArray(data.counterparties)) {
        users = data.counterparties;
      } else if (Array.isArray(data)) {
        users = data;
      }

      if (!Array.isArray(users)) {
        return [];
      }

      if (users.length === 0) {
        return [];
      }

      // The backend already filters counterparties correctly
      // Transform and validate the data
      const validCounterparties = users.filter((u) => {
        // Treat as sub account if flag set OR parentAccountId present (backend sometimes omits isSubAccount flag)
        const isSub = u.isSubAccount === true || !!u.parentAccountId;
        if (isSub) return false; // Exclude all sub accounts
        // Only allow primary Direct Investors or Custodians
        if (u.isDirectInvestor || u.isCustodian || u.role === 'custodian') return true;
        return false;
      });

      const transformedCounterparties = validCounterparties.map(u => ({
        id: u.id || u._id, // fallback to _id if id missing
        name: deriveDisplayName(u),
        lei: u.lei || u.corporateInfo?.lei || '',
        type: u.isCustodian || u.role === 'custodian' ? 'Custodian' : (u.accountType === 'corporate' ? 'Corporate Investor' : 'Individual Investor'),
        email: u.email,
        role: u.role,
        status: u.isActive ? 'Active' : 'Inactive',
        isActive: !!u.isActive
      })).filter(c => c.id); // ensure id exists after fallback

      return transformedCounterparties;
    }
  });

  // Bond owners (for Buy direction to list valid sellers)
  const { data: bondOwnersData = [], isLoading: bondOwnersLoading } = useQuery({
    queryKey: ['bond-owners', transferForm.bondId],
    queryFn: () => custodianAPI.getBondOwners(transferForm.bondId, { limit: 200 }),
    enabled: transferForm.transferDirection === 'Buy' && !!transferForm.bondId,
    staleTime: 30000,
    select: (resp) => {
      const raw = resp?.data?.data || resp?.data || [];
      if (!Array.isArray(raw)) return [];
      return raw.map(o => {
        const leiValue = o.lei || o.corporateInfo?.lei || '';
        return {
          id: o.id || o._id || o.investorId || o.clientId,
          name: o.name || o.investorName || o.displayName || 'Unknown Investor',
          lei: leiValue,
          type: o.type || (o.accountType === 'corporate' ? 'Corporate Investor' : 'Individual Investor'),
          allocation: o.tokenAmount || o.currentHolding || o.allocation || 0,
          isActive: o.isActive !== undefined ? !!o.isActive : true
        };
      });
    }
  });

  // Bond allocation table (investor allocations) – used for Buy direction counterparties
  const { data: bondAllocationInvestors = [], isLoading: bondAllocationLoading } = useQuery({
    queryKey: ['bond-allocation-investors', transferForm.bondId],
    enabled: transferForm.transferDirection === 'Buy' && !!transferForm.bondId,
    queryFn: () => subscriptionAPI.getBondAllocation(transferForm.bondId),
    staleTime: 30000,
    select: (resp) => {
      // Attempt to normalize a wide variety of possible backend shapes
      // Common patterns: { data: { data: { investorAllocations: [...] } } } OR { data: { investorAllocations: [...] } }
      const root = resp?.data?.data || resp?.data || resp;
      if (!root) return [];
      let allocationsArr = [];
      if (Array.isArray(root)) allocationsArr = root; // direct array
      else if (Array.isArray(root.investorAllocations)) allocationsArr = root.investorAllocations;
      else if (Array.isArray(root.allocations)) allocationsArr = root.allocations;
      else if (Array.isArray(root.data)) allocationsArr = root.data;
      // Map to unified investor objects
      return (allocationsArr || []).map(a => {
        const investor = a.investor || a.user || {};
        const leiValue = a.lei || investor.lei || investor.corporateInfo?.lei || '';
        return {
          id: a.investorId || investor._id || investor.id || a._id,
          // Fallback order of name sources
          name: a.investorName || investor.name || investor.displayName || investor.fullName || investor.email || 'Unknown Investor',
          lei: leiValue,
          type: (investor.accountType === 'corporate' || a.accountType === 'corporate') ? 'Corporate Investor' : 'Individual Investor',
          allocation: a.tokenAmount || a.currentHolding || a.allocation || a.amountAllocated || 0,
          isActive: investor.isActive !== undefined ? !!investor.isActive : (a.isActive !== undefined ? !!a.isActive : true)
        };
      }).filter(i => i.id && i.allocation > 0);
    }
  });

  // ===== Fetch Secondary Transfers (table = secondary_transfers) =====
  const { data: transfersData = [], isLoading: transfersLoading, error: transfersError } = useQuery({
    queryKey: ['custodian-secondary-transfers', statusFilter, typeFilter],
    queryFn: () => secondaryTransfersAPI.getTransfers({
      status: statusFilter === 'all' ? undefined : statusFilter,
      transferType: typeFilter === 'all' ? undefined : typeFilter,
      limit: 100,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    }),
    select: (resp) => {
      const root = resp?.data?.data || resp?.data || resp;
      const list = root?.transfers || root?.data || [];
      if (!Array.isArray(list)) return [];
      return list.map(t => {
        // Determine direction from perspective of client (if clientId present)
        const transferDirection = t.transferType === 'Sell' ? 'Sell' : 'Buy';
        const clientObj = t.clientId && typeof t.clientId === 'object' ? t.clientId : null;
        const counterpartyObj = t.counterpartyId && typeof t.counterpartyId === 'object' ? t.counterpartyId : null;
        const bondObj = t.bondId && typeof t.bondId === 'object' ? t.bondId : {};
        const safeName = (o) => deriveDisplayName(o);
        return {
          id: t._id,
          transferRequestId: t.transferRequestId || t.requestId,
          clientId: clientObj?._id || t.clientId,
          clientName: clientObj ? safeName(clientObj) : (t.clientName || '—'),
          bondName: bondObj.bondName || t.bondName || 'Unknown Bond',
          bondIsin: bondObj.isin || t.stockCode || t.isin || 'N/A',
          transferType: t.transferType,
          transferDirection,
          counterparty: counterpartyObj?._id || t.counterpartyId,
          counterpartyName: counterpartyObj ? safeName(counterpartyObj) : (t.counterpartyName || 'Unknown'),
          nominalValue: t.nominalValueTransferred,
          tokenAmount: t.nominalValueTransferred, // mirror for UI
          tradeDate: t.tradeDate,
          valueDate: t.valueDate,
          status: t.status,
          approvalStatus: t.approvalStatus || 'Pending',
          price: t.price,
          transferReason: t.transferReason,
          l1ApprovedByName: t.l1ApprovedBy ? safeName(t.l1ApprovedBy) : '',
          l2ApprovedByName: t.l2ApprovedBy ? safeName(t.l2ApprovedBy) : '',
          l1ApprovedById: t.l1ApprovedBy?._id,
          l2ApprovedById: t.l2ApprovedBy?._id
        };
      });
    }
  });

  // L1/L2 approval mutation
  const approveTransferMutation = useMutation({
    mutationFn: ({ id, notes }) => secondaryTransfersAPI.approveTransfer(id, { approvalNotes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-secondary-transfers']);
    },
    onError: (error) => {
      // Attempt to extract a meaningful backend message
      const rawMsg = error?.response?.data?.message || error?.data?.message || error?.message || 'Failed to approve transfer.';
      let msg = rawMsg;
      if (/inactive/i.test(rawMsg)) {
        // Normalize message if backend provided a generic one
        msg = rawMsg.includes('Cannot approve transfer') ? rawMsg : 'Cannot approve transfer: one or more involved accounts are inactive.';
      }
      setModalTitle('Approval Failed');
      setModalMessage(msg);
      setShowErrorModal(true);
      console.warn('[ClientTransfers][Approve][Error]', { rawMsg, parsedMsg: msg, error });
    }
  });

  // React Query v5 uses isPending for mutations (fallback to isLoading for backward compatibility)
  const approving = approveTransferMutation.isPending || approveTransferMutation.isLoading;

  const handleApprove = (transfer, level) => {
    if (!transfer) return;
    // Prevent approval if either party inactive
    if (transfer.clientActive === false || transfer.counterpartyActive === false) {
      setModalTitle('Approval Blocked');
      setModalMessage('Cannot approve: the client or counterparty account is inactive. Reactivate accounts before proceeding.');
      setShowErrorModal(true);
      return;
    }
    if (level === 'L1' && (transfer.approvalStatus === 'L1 Approved' || transfer.approvalStatus === 'L2 Approved')) return;
    if (level === 'L2') {
      if (transfer.approvalStatus !== 'L1 Approved') return; // need L1 first
      if (transfer.l1ApprovedById && transfer.l1ApprovedById === (user?._id || user?.id)) return; // same user cannot L2
      if (transfer.approvalStatus === 'L2 Approved') return;
    }
    approveTransferMutation.mutate({ id: transfer.id, notes: `${level} approval via Custodian UI` });
  };

  // Create secondary transfer mutation (no token transfer until L2 approval)
  const createTransferMutation = useMutation({
    mutationFn: (transferData) => secondaryTransfersAPI.createTransfer(transferData),
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-secondary-transfers']);
      setShowTransferModal(false);
      setModalTitle('Secondary Transfer Created');
      setModalMessage('Secondary transfer created. Token transfer will be generated only after L2 approval.');
      setShowSuccessModal(true);
      setTransferForm({
        clientId: '',
        clientWalletId: '',
        bondId: '',
        transferType: 'Secondary',
        counterpartyId: '',
        nominalValue: '',
        tokenAmount: '',
        price: '',
        tradeDate: new Date().toISOString().split('T')[0],
        valueDate: '',
        description: '',
        paymentReference: '',
        transferReason: '',
        transferDirection: 'Sell'
      });
    },
    onError: (error) => {
      setModalTitle('Secondary Transfer Creation Failed');
      setModalMessage(error.message || 'Error creating secondary transfer request.');
      setShowErrorModal(true);
    }
  });



  // Fetch client's allocated bonds when a client is selected (from CustodianAllocation model)
  const { data: clientAllocatedBondsData, isLoading: clientAllocatedBondsLoading } = useQuery({
    queryKey: ['client-allocated-bonds', transferForm.clientId],
    queryFn: () => custodianAPI.getClientAllocatedBonds(transferForm.clientId),
    enabled: !!transferForm.clientId,
    keepPreviousData: true, // Prevent page reload when switching clients
    staleTime: 30000, // Consider data fresh for 30 seconds to avoid unnecessary refetches
    select: (data) => {
      // Handle the API response structure
      console.log(data);

      const bonds = data?.data?.data || data?.data || [];

      console.log(bonds);


      // The API already returns bonds in the correct format with client allocation data
      return bonds;
    }
  });

  // Fetch selected client's wallets (sub-investor wallets)
  const { data: clientWalletsData, isLoading: clientWalletsLoading, error: clientWalletsError } = useQuery({
    queryKey: ['client-wallets', transferForm.clientId],
    queryFn: () => custodianAPI.getSubInvestorWallets(transferForm.clientId),
    enabled: !!transferForm.clientId,
    staleTime: 30000,
    select: (resp) => {
      const raw = resp?.data?.data || resp?.data || resp || [];
      const wallets = Array.isArray(raw) ? raw : (Array.isArray(raw.wallets) ? raw.wallets : []);
      const subInvestor = resp?.data?.subInvestor || resp?.subInvestor;
      const lei = subInvestor?.corporateInfo?.lei || subInvestor?.lei || '';

      const processedWallets = wallets.map(w => {
        const acctType = w.bankAccountDetails?.accountType;
        const prettyAcct = acctType === 'islamic' ? 'Islamic' : acctType === 'conventional' ? 'Conventional' : undefined;
        return {
          id: w._id || w.id,
          address: w.walletAddress || w.address || w.publicAddress || '',
          accountType: prettyAcct,
          lei: lei,
          // Keep original fallback label but prefer account type descriptor
          label: prettyAcct ? `${prettyAcct} Account` : (w.label || w.name || w.description || 'Wallet')
        };
      }).filter(w => w.id && w.address);

      return processedWallets;
    }
  });

  // Auto-select single wallet if only one available and none chosen
  useEffect(() => {
    if (transferForm.clientId && clientWalletsData && clientWalletsData.length === 1 && !transferForm.clientWalletId) {
      setTransferForm(prev => ({ ...prev, clientWalletId: clientWalletsData[0].id }));
    }
  }, [transferForm.clientId, clientWalletsData, transferForm.clientWalletId]);

  // Close counterparty suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCounterpartySuggestions && !event.target.closest('.counterparty-search-container')) {
        setShowCounterpartySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCounterpartySuggestions]);

  // Auto-clear incompatible bond when switching to an Islamic wallet in Buy mode
  useEffect(() => {
    if (transferForm.transferDirection !== 'Buy') return;
    if (!transferForm.clientWalletId) return; // no wallet yet
    const wallet = clientWalletsData?.find(w => w.id === transferForm.clientWalletId);
    if (!wallet) return;
    if (wallet.accountType === 'Islamic' && transferForm.bondId) {
      const bond = (allFinalizedBonds || []).find(b => b.id === transferForm.bondId);
      if (bond && !(bond.productType || '').toLowerCase().includes('sukuk')) {
        setTransferForm(prev => ({ ...prev, bondId: '', counterpartyId: '' }));
      }
    }
  }, [transferForm.clientWalletId, transferForm.transferDirection, transferForm.bondId, clientWalletsData, allFinalizedBonds]);
  // ================= Helper / Derived Data =================
  // Client allocations list derived from allocated bonds query
  const clientAllocations = useMemo(() => clientAllocatedBondsData || [], [clientAllocatedBondsData]);

  // Allocation loading alias for legacy references
  const allocationsLoading = clientAllocatedBondsLoading;

  const getSelectedClient = () => {
    if (!transferForm.clientId) return null;
    return clientsData?.find(c => c.id === transferForm.clientId) || null;
  };

  const getClientBondAllocation = (bondId) => {
    if (!bondId) return null;
    const alloc = clientAllocations.find(a => a.bondId === bondId || a.bondId?._id === bondId);
    if (!alloc) return null;
    return {
      bondId: alloc.bondId?._id || alloc.bondId,
      currentHolding: alloc.tokenAmount || alloc.currentHolding || 0,
      availableForTransfer: alloc.availableForTransfer !== undefined ? alloc.availableForTransfer : (alloc.tokenAmount || 0),
      allocationPercentage: alloc.allocationPercentage || 0,
      tokenAmount: alloc.tokenAmount || 0
    };
  };

  // Filter client allocations by selected wallet (Sell direction only)
  const getFilteredClientAllocations = () => {
    if (transferForm.transferDirection !== 'Sell') return [];
    // If no wallet chosen yet, return all allocations (original behavior pre-filter)
    if (!transferForm.clientWalletId) return clientAllocations;
    const selectedWallet = clientWalletsData?.find(w => w.id === transferForm.clientWalletId);
    if (!selectedWallet) return [];
    const targetWalletId = selectedWallet.id?.toString();
    if (!targetWalletId) return [];
    return clientAllocations.filter(a => {
      // In allocated-bonds response walletAddress is the wallet ObjectId (string). It may also appear nested.
      const allocWalletRaw = a.walletAddress || a.clientAllocation?.walletAddress;
      const allocWalletId = typeof allocWalletRaw === 'object' ? (allocWalletRaw?._id || allocWalletRaw.id) : allocWalletRaw;
      return allocWalletId?.toString() === targetWalletId;
    });
  };

  const getCustodianBonds = () => {
    // For Sell direction this helper isn't used (sell path uses client allocations)
    if (transferForm.transferDirection !== 'Buy') {
      return allFinalizedBonds || [];
    }
    const all = allFinalizedBonds || [];
    // If no wallet selected yet, show all finalized bonds
    if (!transferForm.clientWalletId) return all;
    const selectedWallet = clientWalletsData?.find(w => w.id === transferForm.clientWalletId);
    if (!selectedWallet) return all;
    // Islamic wallet: restrict to Sukuk only
    if (selectedWallet.accountType === 'Islamic') {
      return all.filter(b => (b.productType || '').toLowerCase().includes('sukuk'));
    }
    // Conventional wallet: show all bonds (no filtering)
    return all;
  };

  // (Removed stray premature table JSX block)

  // Save recently used counterparty to localStorage
  const saveRecentCounterparty = (counterpartyId) => {
    if (!counterpartyId) return;
    try {
      const updated = [
        counterpartyId,
        ...recentlyUsedCounterparties.filter(id => id !== counterpartyId)
      ].slice(0, 5); // Keep only last 5
      setRecentlyUsedCounterparties(updated);
      localStorage.setItem('recentCounterparties', JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save recent counterparty:', error);
    }
  };

  // Filter counterparties based on search term (LEI or name)
  const filterCounterpartiesBySearch = (parties, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return parties;
    const term = searchTerm.toLowerCase().trim();
    return parties.filter(party => {
      const nameMatch = (party.name || '').toLowerCase().includes(term);
      const leiMatch = (party.lei || '').toLowerCase().includes(term);
      const emailMatch = (party.email || '').toLowerCase().includes(term);
      return nameMatch || leiMatch || emailMatch;
    });
  };

  // Get counterparties sorted with recently used at top
  const getSortedCounterpartiesWithRecent = (parties) => {
    if (!parties || parties.length === 0) return [];
    const recentIds = new Set(recentlyUsedCounterparties);
    const recent = parties.filter(p => recentIds.has(p.id));
    const others = parties.filter(p => !recentIds.has(p.id));
    return [...recent, ...others];
  };

  // Get available counterparties based on transfer direction
  const getAvailableCounterparties = () => {
    // For Sell transfers, any direct investor or custodian can be a counterparty (buyer)
    if (transferForm.transferDirection === 'Sell') {
      if (!counterpartiesData) {
        return [];
      }
      const sellCounterparties = counterpartiesData.filter(party => party.id !== transferForm.clientId);
      return sellCounterparties;
    }

    // For Buy transfers, use allocation table investors first; fallback to bond owners API
    if (transferForm.transferDirection === 'Buy' && transferForm.bondId) {
      const source = (bondAllocationInvestors && bondAllocationInvestors.length > 0)
        ? bondAllocationInvestors
        : bondOwnersData || [];
      const currentCustodianId = (user?._id || user?.id || '').toString();
      const clientIdStr = (transferForm.clientId || '').toString();
      const availableSellers = source.filter(owner => {
        const ownerId = (owner.id || owner._id || '').toString();
        // Exclude the selected client itself AND the current custodian user
        return ownerId && ownerId !== clientIdStr && ownerId !== currentCustodianId;
      });
      return availableSellers;
    }

    // Default case - return filtered counterparties
    if (!counterpartiesData) {
      return [];
    }
    const defaultCounterparties = counterpartiesData.filter(party => party.id !== transferForm.clientId);
    return defaultCounterparties;
  };

  // Filter transfer requests
  const filteredTransfers = useMemo(() => {
    if (!Array.isArray(transfersData)) return [];
    const term = searchTerm.toLowerCase();
    return transfersData.filter(t => {
      const matchesClient = clientFilter === 'all' || t.clientId === clientFilter;
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesSearch = !term || [t.bondName, t.bondIsin, t.clientName, t.counterpartyName]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(term));
      return matchesClient && matchesStatus && matchesSearch;
    });
  }, [transfersData, clientFilter, statusFilter, searchTerm]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Confirmation': { icon: Clock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      'Pending L1': { icon: Clock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      'Pending L2': { icon: Clock, bgColor: 'bg-amber-100', textColor: 'text-amber-800' },
      'Approved': { icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      'Confirmed': { icon: CheckCircle, bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      'Completed': { icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      'Rejected': { icon: XCircle, bgColor: 'bg-red-100', textColor: 'text-red-800' },
      'Cancelled': { icon: XCircle, bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
      'Failed': { icon: XCircle, bgColor: 'bg-red-100', textColor: 'text-red-800' }
    };
    const key = status in statusConfig ? status : 'Pending L1';
    const cfg = statusConfig[key];
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bgColor} ${cfg.textColor}`}>
        <Icon className="w-3 h-3 mr-1" />{status}
      </span>
    );
  };

  const getTransferDirectionIcon = (direction) => {
    return direction === 'Sell' ? '📤' : '📥';
  };

  const getTransferTypeIcon = (direction) => {
    return direction === 'Sell' ? '📤' : '📥';
  };

  const handleInputChange = (field, value) => {
    // Prevent selecting inactive client (extra safety in case of manual DOM manipulation)
    if (field === 'clientId' && value) {
      const targetClient = clientsData?.find(c => c.id === value);
      if (targetClient && targetClient.isActive === false) {
        return; // ignore selection of inactive client
      }
    }
    // Prevent selecting inactive counterparty (extra safety)
    if (field === 'counterpartyId' && value) {
      const targetCounterparty = getAvailableCounterparties().find(p => p.id === value);
      if (targetCounterparty && targetCounterparty.isActive === false) {
        return; // ignore selection of inactive counterparty
      }
      // Save to recently used
      saveRecentCounterparty(value);
    }
    setTransferForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset bond and counterparty selection when client changes
    if (field === 'clientId') {
      setTransferForm(prev => ({
        ...prev,
        clientWalletId: '',
        counterpartyWalletId: '',
        bondId: '',
        counterpartyId: '' // Also reset counterparty to avoid selecting the same client
      }));
    }

    if (field === 'clientWalletId') {
      setTransferForm(prev => ({
        ...prev,
        bondId: '', // reset bond because list depends on wallet
        counterpartyWalletId: '',
        counterpartyId: ''
      }));
    }

    // Reset bond and counterparty selection when transfer direction changes
    if (field === 'transferDirection') {
      setTransferForm(prev => ({
        ...prev,
        bondId: '',
        counterpartyWalletId: '',
        counterpartyId: '' // Reset counterparty since available counterparties will change
      }));
    }

    // Reset counterparty selection when bond changes in Buy mode (different bond owners)
    if (field === 'bondId' && transferForm.transferDirection === 'Buy') {
      setTransferForm(prev => ({
        ...prev,
        counterpartyWalletId: '',
        counterpartyId: ''
      }));
    }
  };

  const validateForm = () => {
    const required = ['clientId', 'bondId', 'counterpartyId', 'nominalValue', 'tokenAmount', 'price', 'paymentReference'];
    const hasAllFields = required.every(field => transferForm[field] && transferForm[field].toString().trim() !== '');

    // Additional validation: client and counterparty cannot be the same
    const differentParties = transferForm.clientId !== transferForm.counterpartyId;

    // Transfer direction specific validation
    let validTransfer = true;
    if (transferForm.transferDirection === 'Sell') {
      // For sell transactions: client must have allocation and amount cannot exceed available
      if (transferForm.tokenAmount && transferForm.bondId && transferForm.clientId) {
        const clientAllocation = getClientBondAllocation(transferForm.bondId);
        if (!clientAllocation) {
          validTransfer = false; // Client doesn't own this bond
        } else {
          const tokenAmount = parseFloat(transferForm.tokenAmount);
          if (tokenAmount > clientAllocation.availableForTransfer) {
            validTransfer = false; // Amount exceeds holdings
          }
        }
      }
    }
    // For buy transactions: no additional validation needed (client can buy any amount)

    // Wallet/Bond compatibility: Islamic wallet may only handle Sukuk; conventional handles all
    if (transferForm.clientWalletId && transferForm.bondId) {
      const wallet = clientWalletsData?.find(w => w.id === transferForm.clientWalletId);
      const bond = (allFinalizedBonds || []).find(b => b.id === transferForm.bondId) || clientAllocations.find(a => a.id === transferForm.bondId);
      if (wallet && bond) {
        const isIslamicWallet = wallet.accountType === 'Islamic';
        const isIslamicBond = (bond.productType || '').toLowerCase().includes('sukuk');
        if (isIslamicWallet && !isIslamicBond) {
          return false; // incompatible
        }
      }
    }

    return hasAllFields && differentParties && validTransfer;
  };

  const handleSubmitTransfer = async () => {

    // Validate required fields
    if (!validateForm()) {
      // ...existing code for validation errors...
      if (transferForm.clientWalletId && transferForm.bondId) {
        const wallet = clientWalletsData?.find(w => w.id === transferForm.clientWalletId);
        const bond = (allFinalizedBonds || []).find(b => b.id === transferForm.bondId) || clientAllocations.find(a => a.id === transferForm.bondId);
        const isIslamicWallet = wallet && wallet.accountType === 'Islamic';
        const isIslamicBond = bond && (bond.productType || '').toLowerCase().includes('sukuk');
        if (isIslamicWallet && bond && !isIslamicBond) {
          setModalTitle('Incompatible Wallet');
          setModalMessage('Islamic wallet can only be used with Sukuk bonds. Please select a Sukuk bond or switch to a Conventional wallet.');
          setShowErrorModal(true);
          return;
        }
      }
      if (transferForm.clientId === transferForm.counterpartyId) {
        setModalTitle('Invalid Selection');
        setModalMessage('Client and counterparty cannot be the same investor. Please select a different counterparty.');
        setShowErrorModal(true);
        return;
      }
      if (transferForm.transferDirection === 'Sell') {
        if (transferForm.tokenAmount && transferForm.bondId && transferForm.clientId) {
          const clientAllocation = getClientBondAllocation(transferForm.bondId);
          if (!clientAllocation) {
            setModalTitle('Invalid Transfer');
            setModalMessage('Client does not own this bond and cannot sell it. Please select a bond that the client owns.');
            setShowErrorModal(true);
            return;
          }
          const tokenAmount = parseFloat(transferForm.tokenAmount);
          if (tokenAmount > clientAllocation.availableForTransfer) {
            setModalTitle('Insufficient Holdings');
            setModalMessage(`Transfer amount (${formatCurrency(tokenAmount)}) exceeds client's available holdings (${formatCurrency(clientAllocation.availableForTransfer)}). Please reduce the transfer amount.`);
            setShowErrorModal(true);
            return;
          }
        }
      }
      setModalTitle('Incomplete Form');
      setModalMessage('Please fill in all required fields before submitting the transfer request.');
      setShowErrorModal(true);
      return;
    }

    // Validate tradeDate and valueDate
    const tradeDateObj = new Date(transferForm.tradeDate);
    const valueDateObj = new Date(transferForm.valueDate);
    if (!transferForm.tradeDate || isNaN(tradeDateObj.getTime())) {
      setModalTitle('Invalid Date');
      setModalMessage('Please select a valid Trade Date.');
      setShowErrorModal(true);
      return;
    }
    if (!transferForm.valueDate || isNaN(valueDateObj.getTime())) {
      setModalTitle('Invalid Date');
      setModalMessage('Please select a valid Value Date (Settlement Date).');
      setShowErrorModal(true);
      return;
    }

    // Only create a secondary transfer record; token transfer deferred until L2 approval
    const payload = {
      bondId: transferForm.bondId,
      transferType: transferForm.transferDirection === 'Sell' ? 'Sell' : 'Buy',
      clientId: transferForm.clientId,
      clientWalletId: transferForm.clientWalletId || undefined,
      counterpartyWalletId: transferForm.counterpartyWalletId || undefined,
      clientName: getSelectedClient()?.name,
      counterpartyId: transferForm.counterpartyId,
      counterpartyName: counterpartiesData?.find(c => c.id === transferForm.counterpartyId)?.name,
      nominalValueTransferred: parseFloat(transferForm.nominalValue),
      price: parseFloat(transferForm.price),
      tradeDate: tradeDateObj.toISOString(),
      valueDate: valueDateObj.toISOString(),
      currency: 'MYR',
      transferReason: transferForm.transferReason || `Secondary Market ${transferForm.transferDirection}`,
      paymentReference: (transferForm.paymentReference || '').trim()
    };
    try { await createTransferMutation.mutateAsync(payload); } catch (_) { }
  };

  const handleViewDetails = (transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailsModal(true);
  };

  // Loading states (exclude clientAllocatedBondsLoading to prevent page reload when switching clients)
  if (clientsLoading || finalizedBondsLoading || counterpartiesLoading || transfersLoading) {
    return <LoadingSpinner />;
  }

  // Error states
  if (clientsError || transfersError || counterpartiesError || finalizedBondsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading data</h3>
          <p className="mt-1 text-sm text-gray-500">
            {clientsError?.message || transfersError?.message || counterpartiesError?.message || finalizedBondsError?.message || 'Please try again later'}
          </p>
          {counterpartiesError && (
            <div className="mt-4 p-4 bg-red-50 rounded-md">
              <p className="text-sm text-red-800">
                <strong>Counterparties Error:</strong> {JSON.stringify(counterpartiesError)}
              </p>
            </div>
          )}
          {finalizedBondsError && (
            <div className="mt-4 p-4 bg-red-50 rounded-md">
              <p className="text-sm text-red-800">
                <strong>Finalized Bonds Error:</strong> {JSON.stringify(finalizedBondsError)}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Local lightweight counterparty wallet selector */}
      {/* Placed inside component scope to access hooks */}
      {/** Reusable selector for a counterparty's primary wallet */}
      {(() => {
        // Define inner component only once
        function Selector({ counterpartyId, value, onChange }) {
          const { data, isLoading, isFetching } = useQuery({
            queryKey: ['counterparty-wallet', counterpartyId],
            enabled: !!counterpartyId,
            queryFn: async () => {
              try {
                const resp = await investorAPI.getCounterpartyWallet(counterpartyId);
                // Normalize to inner payload object first
                return resp?.data?.data || resp?.data || resp;
              } catch (e) {
                return null; // swallow and return null (optional wallet)
              }
            },
            staleTime: 30000
          });
          const wallets = useMemo(() => {
            if (!data) return [];
            // Accept multiple possible shapes
            // 1. { wallets: [...] }
            // 2. { wallet: {...} }
            // 3. single wallet object
            // 4. array
            let arr = [];
            if (Array.isArray(data.wallets)) arr = data.wallets;
            else if (data.wallets && typeof data.wallets === 'object') {
              // edge: wallets object with values
              const maybeArr = Object.values(data.wallets).find(v => Array.isArray(v));
              if (Array.isArray(maybeArr)) arr = maybeArr; else arr = [data.wallets];
            } else if (data.wallet) arr = [data.wallet];
            else if (Array.isArray(data)) arr = data;
            else if (Array.isArray(data.data)) arr = data.data; // nested array
            else arr = [data];
            return arr
              .filter(Boolean)
              .map(w => {
                const id = w._id || w.id;
                const address = w.walletAddress || w.address || w.publicAddress || w.blockchainAddress || '';
                const acctTypeRaw = (w.bankAccountDetails?.accountType || w.accountType || '').toLowerCase();
                const label = acctTypeRaw === 'islamic' ? 'Islamic' : 'Primary';
                return id ? { id, address, label } : null;
              })
              .filter(Boolean);
          }, [data]);
          if (isLoading || isFetching) return <div className="text-xs text-gray-500">Loading wallet...</div>;
          if (!wallets.length) return <div className="text-xs text-gray-400">No wallet data</div>;
          return (
            <select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Auto (primary)</option>
              {wallets.map(w => (
                <option key={w.id} value={w.id}>
                  {w.label}: {w.address ? `${w.address.slice(0, 6)}...${w.address.slice(-4)}` : 'ID ' + w.id.slice(-6)}
                </option>
              ))}
            </select>
          );
        }
        // Expose as capitalized component
        // eslint-disable-next-line react/display-name
        ClientTransfers.CounterpartyWalletSelector = Selector;
        return null;
      })()}
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Secondary Transfers</h1>
              <p className="text-sm text-gray-600 mt-1">
                View and manage secondary market transfers that you have initiated. These are transfers you created involving sub-investors or direct transfers.
              </p>
              <p className="text-xs text-blue-600 mt-1">
                💡 All secondary token transfers are shown regardless of status
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowTransferModal(true)}
                disabled={false}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Initiate New Transfer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions for Pending Approvals */}
        {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length > 0 && (
          <div className="bg-blue-50 border border-blue-200  rounded-lg  p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Secondary Token Transfers Awaiting Processing
                  </h2>
                  <p className="text-sm text-gray-600">
                    {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length} transfers in pending states
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length} Pending
              </span>
            </div>

            <div className="space-y-3">
              {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).slice(0, 3).map((transfer) => (
                <div key={transfer.id} className="bg-white rounded-lg p-4 border border-light-gray">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {transfer.clientName} → {transfer.counterpartyName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {transfer.bondName} ({transfer.bondIsin}) • {formatCurrency(transfer.nominalValue)}
                          </p>
                        </div>
                        <div className="text-center">
                          <span className="text-lg text-blue-600">🔄</span>
                          <p className="text-xs text-gray-500">Secondary</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length > 3 && (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-500">
                    +{filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length - 3} more pending transfers (see table below)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Pending Requests Notification */}
        {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-yellow-600 mr-3" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-900">
                  ⚠️ Pending Token Transfers
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length} secondary token transfer{filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length > 1 ? 's' : ''} awaiting processing / approvals.
                </p>
                <div className="mt-2">
                  <p className="text-xs text-yellow-600">
                    💡 Scroll down to the transfer table to view the status of each request.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {filteredTransfers.filter(req => ['Pending Confirmation', 'Pending L1', 'Pending L2'].includes(req.status)).length} Pending
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Search Card - 60% width (3 out of 5 columns) */}
          <div className="card lg:col-span-3">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Bond Name, ISIN, Client, or Counterparty..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Client Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Clients</option>
                {clientsData?.map(client => (
                  <option
                    key={client.id}
                    value={client.id}
                    disabled={client.isActive === false}
                  >
                    {client.name}{client.isActive === false ? ' (Inactive)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Statuses</option>
                <option value="Pending Confirmation">Pending Confirmation</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
                <option value="Failed">Failed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mt-4">
            Showing {filteredTransfers.length} of {transfersData?.length || 0} secondary token transfers
          </p>
        </div>

        {/* Transfer Requests Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-secondary-900">
                Secondary Token Transfer
              </h2>

            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Client/Investor
                  </th>
                  <th className="table-header-cell">
                    Bond (ISIN)
                  </th>
                  <th className="table-header-cell">
                    Transfer Direction
                  </th>
                  <th className="table-header-cell">
                    Counterparty
                  </th>
                  <th className="table-header-cell">
                    Bond Amount
                  </th>
                  <th className="table-header-cell">
                    Nominal Value
                  </th>
                  <th className="table-header-cell">
                    Created Date
                  </th>
                  <th className="table-header-cell">
                    Scheduled Date
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">
                    L1 Approved By
                  </th>
                  <th className="table-header-cell">
                    L2 Approved By
                  </th>
                  <th className="table-header-cell">Approvals / Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransfers.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-12 text-center text-gray-500">
                      <ArrowLeftRight className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No secondary transfers found</p>
                      <p className="text-sm">
                        {(transfersData?.length || 0) === 0
                          ? 'No secondary token transfers involving your custodian wallets yet.'
                          : 'Try adjusting your search or filter criteria.'
                        }
                      </p>
                      {/* Removed create button prompt in empty state for token transfer activity view */}
                    </td>
                  </tr>
                ) : (
                  filteredTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {transfer.clientName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {transfer.bondName}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {transfer.bondIsin}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">

                          <span className="text-sm text-gray-900">{transfer.transferDirection}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{transfer.counterpartyName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {transfer.tokenAmount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(transfer.nominalValue)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(transfer.tradeDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {transfer.valueDate ? formatDate(transfer.valueDate) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(transfer.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transfer.l1ApprovedByName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transfer.l2ApprovedByName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          {(() => {
                            const phase = transfer.approvalStatus;
                            const currentUserId = user?._id || user?.id;
                            const sameApprover = transfer.l1ApprovedById && transfer.l1ApprovedById === currentUserId;
                            if (['Rejected', 'Cancelled', 'Completed'].includes(transfer.status)) return null;
                            if (!phase || phase === 'Pending') {
                              return (
                                <button
                                  onClick={() => handleApprove(transfer, 'L1')}
                                  disabled={approving}
                                  className="btn-primary btn-sm"
                                >
                                  {approving ? '...' : 'Approve L1'}
                                </button>
                              );
                            }
                            if (phase === 'L1 Approved' && !sameApprover) {
                              return (
                                <button
                                  onClick={() => handleApprove(transfer, 'L2')}
                                  disabled={approving}
                                  className="btn-primary btn-sm"
                                >
                                  {approving ? '...' : 'Approve L2'}
                                </button>
                              );
                            }
                            return null;
                          })()}
                          <button
                            onClick={() => handleViewDetails(transfer)}
                            className="text-primary-600 hover:text-primary-700 p-1"
                            title="View transfer details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Initiate Client Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Initiate New Secondary Transfer
              </h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">

              {/* Client Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Selection *
                    {transferForm.clientId && clientAllocatedBondsLoading && (
                      <span className="ml-2 text-xs text-blue-600">
                        <RefreshCw className="inline w-3 h-3 mr-1 animate-spin" />
                        Loading bonds...
                      </span>
                    )}
                  </label>
                  <select
                    value={transferForm.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={clientsLoading}
                  >
                    <option value="">
                      {clientsLoading ? 'Loading clients...' : 'Select a client'}
                    </option>
                    {clientsData?.map(client => (
                      <option
                        key={client.id}
                        value={client.id}
                        disabled={client.isActive === false}
                      >
                        {client.name} ({client.type}){client.isActive === false ? ' - Inactive' : ''}
                      </option>
                    ))}
                  </select>
                  {clientsError && (
                    <p className="mt-1 text-xs text-red-600">
                      Error loading clients: {clientsError.message}
                    </p>
                  )}
                  {!clientsLoading && clientsData?.length === 0 && (
                    <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
                      <p className="text-blue-800 mb-2">
                        No clients found. You need to create sub-investors first.
                      </p>
                      <Link
                        to="/custodian/investor-management"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        → Go to Investor Management to create sub-investors
                      </Link>
                    </div>
                  )}

                  {/* Client Wallet Selection (appears after client chosen) */}
                  {transferForm.clientId && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client Wallet {clientWalletsLoading && (
                          <span className="ml-2 text-xs text-blue-600">
                            <RefreshCw className="inline w-3 h-3 mr-1 animate-spin" /> Loading wallets...
                          </span>
                        )}
                      </label>
                      <select
                        value={transferForm.clientWalletId}
                        onChange={(e) => handleInputChange('clientWalletId', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        disabled={clientWalletsLoading}
                      >
                        <option value="">
                          {clientWalletsLoading
                            ? 'Loading client wallets...'
                            : (clientWalletsData?.length ? 'Select client wallet (optional)' : 'No wallets found for this client')}
                        </option>
                        {clientWalletsData?.map(w => {
                          const hasMultipleWallets = clientWalletsData.length >= 2;
                          const displayLei = w.lei || 'No LEI';
                          const displayText = hasMultipleWallets && w.accountType
                            ? `${displayLei} (${w.accountType})`
                            : displayLei;

                          return (
                            <option key={w.id} value={w.id}>
                              {displayText}
                            </option>
                          );
                        })}
                      </select>
                      {clientWalletsError && (
                        <p className="mt-1 text-xs text-red-600">Error loading wallets: {clientWalletsError.message}</p>
                      )}
                      {!clientWalletsLoading && transferForm.clientId && clientWalletsData?.length === 1 && (
                        <p className="mt-1 text-xs text-green-600">✅ Single wallet auto-selected.</p>
                      )}
                      {!clientWalletsLoading && transferForm.clientId && clientWalletsData?.length > 1 && !transferForm.clientWalletId && (
                        <p className="mt-1 text-xs text-gray-500">Select which client wallet will be used for this transfer (optional).</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Transfer Direction */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Direction *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="Sell"
                        checked={transferForm.transferDirection === 'Sell'}
                        onChange={(e) => handleInputChange('transferDirection', e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Sell (Client is selling tokens they own)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="Buy"
                        checked={transferForm.transferDirection === 'Buy'}
                        onChange={(e) => handleInputChange('transferDirection', e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Buy (Client is buying available tokens)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Bond Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bond Selection *
                </label>

                <select
                  value={transferForm.bondId}
                  onChange={(e) => handleInputChange('bondId', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={(transferForm.transferDirection === 'Sell' ? clientAllocatedBondsLoading : finalizedBondsLoading) || !transferForm.clientId}
                >
                  <option value="">
                    {!transferForm.clientId
                      ? 'Please select a client first'
                      : (transferForm.transferDirection === 'Sell' ? clientAllocatedBondsLoading : finalizedBondsLoading)
                        ? (transferForm.transferDirection === 'Sell' ? 'Loading client bonds...' : 'Loading finalized bonds...')
                        : transferForm.transferDirection === 'Sell'
                          ? (() => {
                            const filtered = getFilteredClientAllocations();
                            if ((clientAllocations?.length || 0) === 0) return 'Client has no bonds to sell';
                            if (transferForm.clientWalletId && filtered.length === 0) return 'No bonds allocated to this wallet';
                            return 'Select a bond to sell';
                          })()
                          : (getCustodianBonds().length === 0 ? 'No finalized bonds available for purchase' : `Select from ${getCustodianBonds().length} available bond${getCustodianBonds().length !== 1 ? 's' : ''} to buy`)
                    }
                  </option>
                  {(() => {
                    if (transferForm.transferDirection === 'Sell') {
                      const allocationsSource = getFilteredClientAllocations();
                      if (!clientAllocations) {
                        return null;
                      }
                      if (clientAllocations.length === 0) {
                        return null;
                      }
                      if (transferForm.clientWalletId && allocationsSource.length === 0) {
                        return null; // no options for this wallet
                      }
                      return allocationsSource.map((bond, index) => {
                        return (
                          <option key={bond.id} value={bond.id}>
                            {bond.bondName} ({bond.isin}) - Available: {formatCurrency(bond.availableForTransfer)}
                          </option>
                        );
                      });
                    } else {
                      const bonds = getCustodianBonds(); // This returns allFinalizedBonds for Buy transfers
                      return bonds.map((bond, index) => {
                        return (
                          <option key={bond.id} value={bond.id}>
                            {bond.bondName} ({bond.isin}) - {bond.productType} - {bond.issuer}
                          </option>
                        );
                      });
                    }
                  })()}
                </select>

                {/* Bond Selection Info */}
                {transferForm.bondId && transferForm.clientId && (
                  <div className="mt-2">
                    {transferForm.transferDirection === 'Sell' ? (
                      getClientBondAllocation(transferForm.bondId) && (
                        <div className="p-2 bg-green-50 rounded border text-xs text-green-800">
                          ✅ Client owns: {formatCurrency(getClientBondAllocation(transferForm.bondId).currentHolding)}
                          • Available to sell: {formatCurrency(getClientBondAllocation(transferForm.bondId).availableForTransfer)}
                          {transferForm.clientWalletId && (
                            <span className="ml-2 text-blue-600">Filtered by selected wallet</span>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="p-2 bg-blue-50 rounded border text-xs text-blue-800">
                        💰 Client is purchasing this bond from custodian allocation
                      </div>
                    )}
                  </div>
                )}

                {/* Transfer Direction Help Text */}
                {transferForm.clientId && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border text-xs text-gray-600">
                    {transferForm.transferDirection === 'Sell' ? (
                      <>
                        <strong>Sell Mode:</strong> Showing bonds the client owns{transferForm.clientWalletId ? ' in the selected wallet' : ''} and can sell.
                        {!allocationsLoading && (clientAllocations?.length === 0) && (
                          <span className="text-orange-600 ml-1">Client has no bond holdings to sell.</span>
                        )}
                        {transferForm.clientWalletId && !allocationsLoading && getFilteredClientAllocations().length === 0 && clientAllocations.length > 0 && (
                          <span className="text-orange-600 ml-1">No bond allocations linked to this wallet.</span>
                        )}
                      </>
                    ) : (
                      <>
                        <strong>Buy Mode:</strong> {transferForm.clientWalletId && clientWalletsData?.find(w => w.id === transferForm.clientWalletId)?.accountType === 'Islamic'
                          ? 'Showing Sukuk bonds only (Islamic wallet selected).'
                          : 'Showing all finalized bonds (status: "Allocation Finalized").'}
                        {!finalizedBondsLoading && getCustodianBonds().length === 0 && (
                          <span className="text-orange-600 ml-1">No eligible bonds found for this wallet type.</span>
                        )}
                        {getCustodianBonds().length > 0 && (
                          <span className="text-green-600 ml-1">{getCustodianBonds().length} bond{getCustodianBonds().length !== 1 ? 's' : ''} available.</span>
                        )}
                        {transferForm.clientWalletId && clientWalletsData?.find(w => w.id === transferForm.clientWalletId)?.accountType === 'Islamic' && (
                          <span className="ml-1 text-xs text-blue-600">(Switch to a Conventional wallet to view all bond types.)</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Counterparty and Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Counterparty LEI Search *
                  </label>
                  {transferForm.counterpartyId && (
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Counterparty Wallet (optional)</label>
                      <ClientTransfers.CounterpartyWalletSelector counterpartyId={transferForm.counterpartyId} value={transferForm.counterpartyWalletId} onChange={(val) => handleInputChange('counterpartyWalletId', val)} />
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mb-2">
                    {transferForm.transferDirection === 'Sell'
                      ? '🔍 Search by LEI code or entity name to find a buyer'
                      : '🔍 Search by LEI code or entity name to find a seller (bond owners only)'
                    }
                  </div>

                  {/* Search Input with Autocomplete */}
                  <div className="relative counterparty-search-container">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={counterpartySearchTerm}
                        onChange={(e) => {
                          setCounterpartySearchTerm(e.target.value);
                          setShowCounterpartySuggestions(true);
                        }}
                        onFocus={() => setShowCounterpartySuggestions(true)}
                        placeholder="Search LEI or entity name..."
                        className="w-full border border-gray-300 rounded-md pl-10 pr-10 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        disabled={counterpartiesLoading || (transferForm.transferDirection === 'Buy' && (bondAllocationLoading || bondOwnersLoading))}
                      />
                      {counterpartySearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setCounterpartySearchTerm('');
                            setTransferForm(prev => ({ ...prev, counterpartyId: '' }));
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Autocomplete Suggestions Dropdown */}
                    {showCounterpartySuggestions && !counterpartiesLoading && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {(() => {
                          const allAvailable = getAvailableCounterparties();
                          const filtered = filterCounterpartiesBySearch(allAvailable, counterpartySearchTerm);
                          const sorted = getSortedCounterpartiesWithRecent(filtered);

                          if (counterpartiesLoading || (transferForm.transferDirection === 'Buy' && (bondAllocationLoading || bondOwnersLoading))) {
                            return (
                              <div className="p-3 text-center text-sm text-gray-500">
                                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                                Loading counterparties...
                              </div>
                            );
                          }

                          if (sorted.length === 0) {
                            return (
                              <div className="p-3 text-center text-sm text-gray-500">
                                {transferForm.transferDirection === 'Buy' && !transferForm.bondId
                                  ? '💡 Please select a bond first'
                                  : allAvailable.length === 0
                                    ? 'No counterparties available'
                                    : 'No matches found - try different search terms'
                                }
                              </div>
                            );
                          }

                          const recentIds = new Set(recentlyUsedCounterparties);
                          let hasRecentSection = false;

                          return (
                            <>
                              {sorted.map((party, index) => {
                                const isRecent = recentIds.has(party.id);
                                const showRecentHeader = isRecent && !hasRecentSection;
                                if (showRecentHeader) hasRecentSection = true;

                                return (
                                  <React.Fragment key={party.id}>
                                    {showRecentHeader && (
                                      <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100 text-xs font-medium text-blue-700 sticky top-0">
                                        ⭐ Recently Used
                                      </div>
                                    )}
                                    {!isRecent && index === sorted.findIndex(p => !recentIds.has(p.id)) && hasRecentSection && (
                                      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600 sticky top-0">
                                        All Counterparties
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleInputChange('counterpartyId', party.id);
                                        setCounterpartySearchTerm(party.name);
                                        setShowCounterpartySuggestions(false);
                                      }}
                                      disabled={party.isActive === false}
                                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${party.isActive === false ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                        } ${transferForm.counterpartyId === party.id ? 'bg-blue-50' : ''}`}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium text-gray-900 truncate">
                                              {party.name}
                                            </span>
                                            {/* Account Type Badge */}
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${party.type?.includes('Custodian')
                                                ? 'bg-purple-100 text-purple-800'
                                                : party.type?.includes('Corporate')
                                                  ? 'bg-blue-100 text-blue-800'
                                                  : 'bg-green-100 text-green-800'
                                              }`}>
                                              {party.type}
                                            </span>
                                            {/* Status Badge */}
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${party.isActive === false
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-green-100 text-green-800'
                                              }`}>
                                              {party.isActive === false ? '⚠️ Inactive' : '✓ Active'}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-600 space-y-0.5">
                                            <div className="flex items-center gap-1">
                                              <span className="font-medium">LEI:</span>
                                              <span className="font-mono">{party.lei || '-'}</span>
                                            </div>
                                            {party.email && (
                                              <div className="flex items-center gap-1">
                                                <span className="font-medium">Email:</span>
                                                <span className="truncate">{party.email}</span>
                                              </div>
                                            )}
                                            {transferForm.transferDirection === 'Buy' && party.allocation > 0 && (
                                              <div className="flex items-center gap-1 text-green-700">
                                                <span className="font-medium">Owns:</span>
                                                <span>{formatCurrency(party.allocation)}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  </React.Fragment>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Selected Counterparty Display */}
                  {transferForm.counterpartyId && (
                    <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                      {(() => {
                        const selected = getAvailableCounterparties().find(p => p.id === transferForm.counterpartyId);
                        if (!selected) return null;
                        return (
                          <div className="text-xs">
                            <div className="font-medium text-blue-900 mb-1">Selected: {selected.name}</div>
                            <div className="text-blue-700 space-y-0.5">
                              <div>LEI: <span className="font-mono">{selected.lei || '-'}</span></div>
                              <div>Type: {selected.type}</div>
                              <div>Status: {selected.isActive === false ? '⚠️ Inactive' : '✓ Active'}</div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Helper text */}
                  {!counterpartiesLoading && !bondOwnersLoading && (
                    <>
                      {transferForm.transferDirection === 'Sell' && getAvailableCounterparties().length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">
                          {transferForm.clientId
                            ? 'No other sub-investors available as buyers'
                            : 'No sub-investors found. Please add sub-investors first.'
                          }
                        </p>
                      )}
                      {transferForm.transferDirection === 'Buy' && transferForm.bondId && getAvailableCounterparties().length === 0 && (
                        <p className="mt-1 text-xs text-orange-600">
                          No investors with allocation/holdings found for this bond.
                        </p>
                      )}
                      {transferForm.transferDirection === 'Buy' && !transferForm.bondId && (
                        <p className="mt-1 text-xs text-blue-600">
                          💡 Select a bond first to see which investors can sell it
                        </p>
                      )}
                      {transferForm.transferDirection === 'Buy' && transferForm.bondId && getAvailableCounterparties().length > 0 && (
                        <p className="mt-1 text-xs text-green-600">
                          ✅ {getAvailableCounterparties().length} investor{getAvailableCounterparties().length > 1 ? 's' : ''} eligible to sell (based on allocation/holdings)
                        </p>
                      )}
                    </>
                  )}
                  {transferForm.clientId && transferForm.counterpartyId === transferForm.clientId && (
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Client and counterparty cannot be the same investor
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nominal Value Transferred *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={transferForm.nominalValue}
                      onChange={(e) => handleInputChange('nominalValue', e.target.value)}
                      placeholder="Enter nominal value"
                      max={transferForm.transferDirection === 'Sell' &&
                        transferForm.clientId && transferForm.bondId && getClientBondAllocation(transferForm.bondId) ?
                        getClientBondAllocation(transferForm.bondId)?.availableForTransfer : undefined}
                      step="0.01"
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 ${transferForm.transferDirection === 'Sell' &&
                        transferForm.nominalValue && transferForm.bondId && transferForm.clientId &&
                        getClientBondAllocation(transferForm.bondId) &&
                        parseFloat(transferForm.nominalValue) > getClientBondAllocation(transferForm.bondId).availableForTransfer
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      required
                    />
                    {transferForm.transferDirection === 'Sell' &&
                      transferForm.clientId && transferForm.bondId && getClientBondAllocation(transferForm.bondId) && (
                        <button
                          type="button"
                          onClick={() => handleInputChange('nominalValue', getClientBondAllocation(transferForm.bondId).availableForTransfer.toString())}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                        >
                          Max
                        </button>
                      )}
                  </div>
                  {transferForm.transferDirection === 'Sell' &&
                    transferForm.clientId && transferForm.bondId && getClientBondAllocation(transferForm.bondId) && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border text-xs text-blue-800">
                        Maximum sellable: {formatCurrency(getClientBondAllocation(transferForm.bondId).availableForTransfer)}
                        {getClientBondAllocation(transferForm.bondId).allocationPercentage && (
                          <span className="ml-2">({getClientBondAllocation(transferForm.bondId).allocationPercentage}% allocation)</span>
                        )}
                      </div>
                    )}
                  {transferForm.transferDirection === 'Buy' && (
                    <div className="mt-2 p-2 bg-green-50 rounded border text-xs text-green-800">
                      💰 Client is purchasing - enter the nominal value they wish to buy
                    </div>
                  )}
                  {transferForm.transferDirection === 'Sell' &&
                    transferForm.clientId && transferForm.bondId && !getClientBondAllocation(transferForm.bondId) && (
                      <div className="mt-2 p-2 bg-red-50 rounded border text-xs text-red-800">
                        ❌ Client has no holdings for this bond - cannot sell
                      </div>
                    )}
                  {transferForm.transferDirection === 'Sell' &&
                    transferForm.nominalValue && transferForm.bondId && transferForm.clientId &&
                    getClientBondAllocation(transferForm.bondId) &&
                    parseFloat(transferForm.nominalValue) > getClientBondAllocation(transferForm.bondId).availableForTransfer && (
                      <p className="mt-1 text-xs text-red-600">
                        ⚠️ Amount exceeds available holdings of {formatCurrency(getClientBondAllocation(transferForm.bondId).availableForTransfer)}
                      </p>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bond Amount *
                  </label>
                  <input
                    type="number"
                    value={transferForm.tokenAmount}
                    onChange={(e) => handleInputChange('tokenAmount', e.target.value)}
                    placeholder="Enter number of tokens"
                    step="1"
                    min="1"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Number of tokens to transfer (usually equals nominal value for most bonds)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price *
                  </label>
                  <input
                    type="number"
                    value={transferForm.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="Agreed-upon price"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Price per unit for the transfer
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trade Date *
                  </label>
                  <input
                    type="date"
                    value={transferForm.tradeDate}
                    onChange={(e) => handleInputChange('tradeDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Value Date (Settlement Date) *
                  </label>
                  <input
                    type="date"
                    value={transferForm.valueDate}
                    onChange={(e) => handleInputChange('valueDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Reference *
                  </label>
                  <input
                    type="text"
                    value={transferForm.paymentReference}
                    onChange={(e) => handleInputChange('paymentReference', e.target.value)}
                    placeholder="Reference for off-chain cash settlement"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Transfer Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Reason (Optional)
                </label>
                <textarea
                  value={transferForm.transferReason}
                  onChange={(e) => handleInputChange('transferReason', e.target.value)}
                  rows={3}
                  placeholder="Brief description of the reason for transfer (e.g., 'Client portfolio rebalancing', 'Strategic investment')"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Confirmation */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="confirm-authorization"
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="confirm-authorization" className="text-sm text-gray-700">
                  I confirm that this transfer request is authorized by my client and all information is accurate.
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTransfer}
                disabled={!validateForm() || createTransferMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createTransferMutation.isLoading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Details Modal */}
      {showDetailsModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 pb-0">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Transfer Request Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 h-6 w-6"
                >
                  <X className="h-6 w-6" style={{ color: '#9ca3af' }} />
                </button>
              </div>
            </div>

            {/* Separator Line */}
            <div className="border-b border-light-gray"></div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Transfer Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getTransferTypeIcon(selectedTransfer.transferDirection)}</span>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Secondary Transfer Request
                      </h4>
                      <p className="text-sm text-gray-600">
                        Request ID: {selectedTransfer.transferRequestId || selectedTransfer.id}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(selectedTransfer.status)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Nominal Value</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(selectedTransfer.nominalValue)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Price</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(selectedTransfer.price)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Value</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(selectedTransfer.nominalValue * selectedTransfer.price)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Parties Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-3">
                    {selectedTransfer.transferDirection === 'Sell' ? 'Seller (Client)' : 'Buyer (Client)'}
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-blue-700">Name</p>
                      <p className="text-sm font-medium text-blue-900">{selectedTransfer.clientName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">Client ID</p>
                      <p className="text-sm font-mono text-blue-800">{selectedTransfer.clientId}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-green-900 mb-3">
                    {selectedTransfer.transferDirection === 'Sell' ? 'Buyer (Counterparty)' : 'Seller (Counterparty)'}
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-green-700">Name</p>
                      <p className="text-sm font-medium text-green-900">{selectedTransfer.counterpartyName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-700">Counterparty ID</p>
                      <p className="text-sm font-mono text-green-800">{selectedTransfer.counterparty}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bond Information */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h5 className="text-sm font-medium text-purple-900 mb-3">Bond Information</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-purple-700">Bond Name</p>
                    <p className="text-sm font-medium text-purple-900">{selectedTransfer.bondName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700">ISIN</p>
                    <p className="text-sm font-mono text-purple-800">{selectedTransfer.bondIsin}</p>
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-sm font-medium text-gray-900 mb-3">Important Dates</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-sm text-gray-600">Trade Date</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(selectedTransfer.tradeDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-sm text-gray-600">Value Date (Settlement)</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(selectedTransfer.valueDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-gray-900 mb-3">Additional Information</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-sm text-gray-600">Initiated By</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedTransfer.initiatedByName || 'Custodian'}
                      </span>
                    </div>
                    {selectedTransfer.paymentReference && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Payment Reference</span>
                        <span className="text-sm font-mono text-gray-900">
                          {selectedTransfer.paymentReference}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Transfer Reason */}
              {selectedTransfer.transferReason && (
                <div>
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Transfer Reason</h5>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700">{selectedTransfer.transferReason}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="p-6 pt-6 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                {modalTitle}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {modalMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <XCircle className="h-8 w-8 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                {modalTitle}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {modalMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Loading Modal */}
      {approving && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-2xl animate-fade-in">
            <div className="flex items-center mb-4">
              <div className="relative mr-3">
                <div className="w-8 h-8 rounded-full border-4 border-blue-100"></div>
                <div className="w-8 h-8 rounded-full border-4 border-blue-600 border-t-transparent animate-spin absolute inset-0" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Processing Approval</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Please wait while the transfer approval is being recorded.
            </p>
            <p className="text-xs text-gray-400">This may take a few seconds…</p>
          </div>
        </div>
      )}

      {/* Page-level logout modal removed */}
    </div>
  );
};

export default ClientTransfers; 